import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import  { reissueToken } from "../services/authenticationService";
import MSALUser from "../domains/MSALUser";

import {
  getItemFromLocalStorage,
  setItemInLocalStorage,
} from "../config/localStorage";

const axiosConfig = {
  baseURL: process.env.REACT_APP_API_URL,
};

export function createAxiosInstance(apiAccessToken?: string) {
  const config = apiAccessToken
    ? {
      ...axiosConfig,
      headers: {
        Authorization: `Bearer ${apiAccessToken}`,
      },
    }
    : axiosConfig;

  const instance = axios.create(config);

  return instance;
}

export const apiInstance = createAxiosInstance();

apiInstance.interceptors.request.use(
  async (config: any) => {
    const user = getItemFromLocalStorage<MSALUser>("AuthContext", null);
    const { apiAccessToken } = user;

    const response = await reissueToken();

    const isTokenInvalid = response!.apiAccessToken !== apiAccessToken;

    if (
      config.headers["x-ms-blob-type"] &&
      (config.headers["Content-Type"].includes("application") ||
        config.headers["Content-Type"].includes("message"))
    ) {
      return config;
    }

    if (!apiAccessToken || isTokenInvalid) {
      config.headers = {
        Authorization: `Bearer ${response.apiAccessToken}`,
      };

      setItemInLocalStorage("AuthContext", {
        ...user,
        apiAccessToken: response.apiAccessToken,
        apiAccessTokenExpiresOn: response?.apiAccessTokenExpiresOn?.toString(),
      });
    } else {
      config.headers = {
        Authorization: `Bearer ${user.apiAccessToken}`,
      };
    }
    return config;
  },
  (error: any) => {
    Promise.reject(error);
  },
);

apiInstance.interceptors.response.use(
  (response: any) => {
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config;

    const isUserUnauthorized =
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      !originalRequest?._retry;

    if (isUserUnauthorized) {
      originalRequest._retry = true;

      const response = await reissueToken();
      apiInstance.defaults.headers.common.Authorization = `Bearer ${response.apiAccessToken}`;

      const newInstance = await await apiInstance(originalRequest);
      return newInstance;
    }

    return Promise.reject(error);
  },
);

export const iffApi = createApi({
  reducerPath: "iffApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (header) => {
      const user = getItemFromLocalStorage<MSALUser>("AuthContext", null);
      if (user && user.apiAccessToken) {
        const { apiAccessToken } = user;
        header.set("Authorization", `Bearer ${apiAccessToken}`);
      }
    }
  }),
  endpoints: (builder) => ({
    getCountries: builder.query<any, void>({
      query: () => "countries",
    }),
    getPorts: builder.query<any, void>({
      query: () => "ports",
    }),
    getRequirements: builder.query<any, any>({
      query: () => "requirements"
    }),
    getProducts: builder.query<any, void>({
      query: () => "products",
    }),
    getRequirementsFilterCount: builder.query<any, void>({
      query: () => "requirements/count?filter[status]=all",
      transformResponse: (response) => {
        const isCompleted = (status: string) =>
          status === "approved" ||
          status === "approved_with_restrictions" ||
          status === "rejected" ||
          status === "bought";
        const isToFeedStockTrading = (status: string) =>
          status === "request_extra_information" || status === "draft";
        const isToPlanningAndScheduling = (status: string) =>
          status === "technical_approved" ||
          status === "technical_rejected" ||
          status === "technical_approved_with_restrictions" ||
          status === "planning_evaluation" ||
          status === "missing_information";
        const isToProcessEvaluation = (status: string) => status === "process_evaluation";
        return Object.keys(response).reduce(
          (counter, status) => {
            counter.all += response[status].count;

            if (isCompleted(status)) {
              counter.concluded += response[status].count;
            }

            if (isToFeedStockTrading(status)) {
              counter.feedstockTrading += response[status].count;
            }
            if (isToPlanningAndScheduling(status)) {
              counter.planningAndScheduling += response[status].count;
            }

            if (isToProcessEvaluation(status)) {
              counter.processEngineering += response[status].count;
            }

            return counter;
          },
          {
            all: 0,
            feedstockTrading: 0,
            concluded: 0,
            processEngineering: 0,
            planningAndScheduling: 0,
          } as Record<string, number>,
        );
      }
    }),
    getRankingFileLink: builder.query<any, string>({
      query: (fileId) => `pdfs-general/${fileId}`,
    }),
    getOpex: builder.query<any, void>({
      query: () => "opexs/",
      transformResponse: (response: any) => {
        return response.rows;
      }
    }),
    getSystems: builder.query<any, void>({
      query: () => "systems/",
    }),
    getSystemsRange: builder.query<any, string>({
      query: (productId) => `systems/range/${productId}`,
    }),
  }),
});

export const { useGetCountriesQuery, useGetPortsQuery, useGetSystemsQuery, useGetProductsQuery, useGetRequirementsQuery, useGetRequirementsFilterCountQuery, useGetOpexQuery, useGetRankingFileLinkQuery, useGetSystemsRangeQuery, usePrefetch } = iffApi;
